import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getLang, t } from '../i18n';
import { hasBusinessHubFeatureAccess } from '../utils/verificationAccess';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const ui = (de, en) => (getLang() === 'en' ? en : de);

function normalizeUtfText(value) {
  return String(value || '')
    .replace(/\u00C3\u0192\u00C2\u00A4/g, 'ä')
    .replace(/\u00C3\u0192\u00C2\u00B6/g, 'ö')
    .replace(/\u00C3\u0192\u00C2\u00BC/g, 'ü')
    .replace(/\u00C3\u0192\u00E2\u20AC\u017E/g, 'Ä')
    .replace(/\u00C3\u0192\u00E2\u20AC\u201C/g, 'Ö')
    .replace(/\u00C3\u0192\u00C5\u201C/g, 'Ü')
    .replace(/\u00C3\u0192\u00C5\u00B8/g, 'ß')
    .replace(/\u00C3\u00A4/g, 'ä')
    .replace(/\u00C3\u00B6/g, 'ö')
    .replace(/\u00C3\u00BC/g, 'ü')
    .replace(/\u00C3\u201E/g, 'Ä')
    .replace(/\u00C3\u2013/g, 'Ö')
    .replace(/\u00C3\u0152/g, 'Ü')
    .replace(/\u00C3\u0178/g, 'ß')
    .replace(/\u00E2\u20AC\u201C/g, '–')
    .replace(/\u00E2\u20AC\u201D/g, '—')
    .replace(/\u00E2\u20AC\u017E/g, '„')
    .replace(/\u00E2\u20AC\u0153/g, '“')
    .replace(/\u00E2\u20AC\u009D/g, '”')
    .replace(/\u00E2\u20AC\u02DC/g, '‘')
    .replace(/\u00E2\u20AC\u2122/g, '’')
    .replace(/\u00EF\u00BB\u00BF/g, '')
    .replace(/\uFFFD/g, 'ü')
    .trim();
}

function normalizeDocumentLabel(value) {
  return normalizeUtfText(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

const DOCUMENT_DEFINITIONS = [
  { label: ui('Ausweis Vorderseite', 'ID front side'), fieldName: 'id_front' },
  { label: ui('Ausweis Rückseite', 'ID back side'), fieldName: 'id_back' },
  { label: 'Selfie', fieldName: 'selfie' },
  { label: ui('Gewerbeanmeldung', 'Trade registration'), fieldName: 'gewerbeanmeldung' },
  { label: ui('Handelsregisterauszug', 'Commercial register extract'), fieldName: 'handelsregisterauszug' },
  { label: ui('Vollmacht', 'Power of attorney'), fieldName: 'vollmacht' },
  { label: ui('Unternehmensdokument', 'Business document'), fieldName: 'business_doc' },
  { label: ui('Zusatzdokument', 'Additional document'), fieldName: 'extra_doc' },
];

const DOCUMENT_DEFINITION_MAP = DOCUMENT_DEFINITIONS.reduce((acc, item) => {
  acc[normalizeDocumentLabel(item.label)] = item;
  return acc;
}, {});

[
  ['Ausweis Vorderseite', 'ID front side', 'id_front'],
  ['Ausweis Rückseite', 'ID back side', 'id_back'],
  ['Selfie', 'Selfie', 'selfie'],
  ['Gewerbeanmeldung', 'Trade registration', 'gewerbeanmeldung'],
  ['Handelsregisterauszug', 'Commercial register extract', 'handelsregisterauszug'],
  ['Vollmacht', 'Power of attorney', 'vollmacht'],
  ['Unternehmensdokument', 'Business document', 'business_doc'],
  ['Zusatzdokument', 'Additional document', 'extra_doc'],
].forEach(([de, en, fieldName]) => {
  const definition = DOCUMENT_DEFINITIONS.find((item) => item.fieldName === fieldName);
  DOCUMENT_DEFINITION_MAP[normalizeDocumentLabel(de)] = definition;
  DOCUMENT_DEFINITION_MAP[normalizeDocumentLabel(en)] = definition;
});

function normalizeCustomerType(customerType) {
  const normalized = normalizeUtfText(customerType).toLowerCase();

  if (['unternehmen', 'gewerbe', 'einzelunternehmen', 'einzelunternehmer', 'kapitalgesellschaft', 'firma', 'gmbh', 'ug', 'ag', 'verein', 'e.v.', 'e. v.', 'ev', 'ausland', 'foreign_company'].includes(normalized)) return 'unternehmen';
  if (['gruendung', 'gründung', 'in-gruendung', 'in_gruendung', 'gruendung_geplant', 'gründung geplant'].includes(normalized)) return 'gruendung';
  if (['freelancer', 'freiberufler', 'freiberuflich', 'freiberuflerin'].includes(normalized)) {
    return 'freelancer';
  }

  return 'privat';
}

function getBusinessUploadLabel(customerType) {
  const normalized = normalizeCustomerType(customerType);

  if (normalized === 'freelancer') {
    return t('freelancer_proof');
  }

  return t('business_doc') ;
}


function getUploadFieldLabel(field) {
  const labels = {
    id_front: t('id_front'),
    id_back: t('id_back'),
    selfie: t('selfie'),
    gewerbeanmeldung: t('trade_registration'),
    handelsregisterauszug: t('commercial_register_extract'),
    vollmacht: t('power_of_attorney'),
    business_doc: getBusinessUploadLabel('unternehmen'),
    extra_doc: t('extra_doc'),
  };

  return labels[field?.fieldName] || field?.label || '';
}

const INITIAL_FILES = {
  id_front: null,
  id_back: null,
  selfie: null,
  gewerbeanmeldung: null,
  handelsregisterauszug: null,
  vollmacht: null,
  business_doc: null,
  extra_doc: null,
};

const INITIAL_AGREEMENT_DATA = {
  company_name: '',
  legal_form: '',
  representative_name: '',
  business_activity: '',
  company_street: '',
  company_postal_code: '',
  company_city: '',
  company_country: '',
  register_court: '',
  register_number: '',
  tax_number: '',
  vat_id: '',
  planned_foundation_date: '',
};

const AGREEMENT_FIELD_LABELS = {
  company_name: ui('Firmenname / geplanter Firmenname', 'Company name / planned company name'),
  legal_form: ui('Rechtsform', 'Legal form'),
  representative_name: ui('Vertreter', 'Representative'),
  business_activity: ui('Branche / Tätigkeit', 'Industry / business activity'),
  company_street: ui('Straße', 'Street'),
  company_postal_code: ui('PLZ', 'Postal code'),
  company_city: ui('Ort', 'City'),
  company_country: ui('Land', 'Country'),
  register_court: ui('Registergericht', 'Register court'),
  register_number: ui('Registernummer', 'Register number'),
  tax_number: ui('Steuernummer', 'Tax number'),
  vat_id: ui('USt-IdNr.', 'VAT ID'),
  planned_foundation_date: ui('Geplantes Gründungsdatum', 'Planned formation date'),
};

function normalizeAgreementData(data) {
  const source = data || {};
  return Object.keys(INITIAL_AGREEMENT_DATA).reduce((acc, key) => {
    acc[key] = normalizeUtfText(source[key]);
    return acc;
  }, { ...INITIAL_AGREEMENT_DATA });
}

function getIdentityStatusMeta(status) {
  const value = normalizeUtfText(status).toLowerCase();

  const map = {
    new: {
      label: t('status_new'),
      tone: 'neutral',
      description: t('status_new_desc'),
    },
    submitted: {
      label: t('status_submitted'),
      tone: 'info',
      description: t('identity_submitted_desc'),
    },
    in_review: {
      label: t('status_in_review'),
      tone: 'info',
      description: t('identity_in_review_desc'),
    },
    additional_requested: {
      label: t('status_additional_requested'),
      tone: 'warning',
      description: t('identity_additional_requested_desc'),
    },
    approved: {
      label: t('status_approved'),
      tone: 'success',
      description: t('identity_approved_desc'),
    },
    rejected: {
      label: t('status_rejected'),
      tone: 'danger',
      description: t('identity_rejected_desc'),
    },
  };

  return map[value] || {
    label: status || '-',
    tone: 'neutral',
    description: t('identity_status_default_desc'),
  };
}

function getBusinessStatusMeta(status) {
  const value = normalizeUtfText(status).toLowerCase();

  const map = {
    not_required: {
      label: t('status_not_required'),
      tone: 'neutral',
      description: t('business_not_required_desc'),
    },
    pending: {
      label: t('status_pending'),
      tone: 'warning',
      description: t('business_pending_desc'),
    },
    submitted: {
      label: t('status_submitted'),
      tone: 'info',
      description: t('business_submitted_desc'),
    },
    in_review: {
      label: t('status_in_review'),
      tone: 'info',
      description: t('business_in_review_desc'),
    },
    approved: {
      label: t('status_approved'),
      tone: 'success',
      description: t('business_approved_desc'),
    },
    rejected: {
      label: t('status_rejected'),
      tone: 'danger',
      description: t('business_rejected_desc'),
    },
  };

  return map[value] || {
    label: status || '-',
    tone: 'neutral',
    description: t('business_status_default_desc'),
  };
}


function getRequestedDocumentDisplayLabel(label) {
  const definition = DOCUMENT_DEFINITION_MAP[normalizeDocumentLabel(label)];

  if (definition) {
    return getUploadFieldLabel(definition);
  }

  return label;
}

function StatusBadge({ label, tone }) {
  return <span className={`status-pill status-pill--${tone}`}>{label}</span>;
}

function Portal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState(INITIAL_FILES);
  const [agreementData, setAgreementData] = useState(INITIAL_AGREEMENT_DATA);
  const [activeTab, setActiveTab] = useState('overview');
  const [portalRequestSubmitting, setPortalRequestSubmitting] = useState('');
  const [displayDraft, setDisplayDraft] = useState({
    display_name: '',
    display_note: '',
  });
  const [displayLogo, setDisplayLogo] = useState(null);

  useEffect(() => {
    const fetchStatus = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await fetch(`${API_URL}/portal/${token}`);
        const json = await response.json();

        if (response.ok) {
          setData(json);
          setAgreementData(normalizeAgreementData(json.usage_agreement_data));
          setDisplayDraft({
            display_name: normalizeUtfText(json.display_profile?.display_name || json.customer?.company_name || json.customer?.name || ''),
            display_note: normalizeUtfText(json.display_profile?.display_note || ''),
          });
        } else {
          setError(json.error || t('error_fetch'));
        }
      } catch (err) {
        console.error(err);
        setError(t('error_fetch'));
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [token]);

  const customerType = useMemo(() => normalizeCustomerType(data?.customer?.customer_type), [data]);
  const businessUploadLabel = useMemo(() => getBusinessUploadLabel(customerType), [customerType]);
  const showAgreementUpdateFields = Boolean(
    data?.initial_submission_completed && data?.is_planned_founding_followup
  );

  const requestedDocuments = useMemo(() => {
    return Array.isArray(data?.followup_request?.requested_documents)
      ? data.followup_request.requested_documents.map((item) => normalizeUtfText(item))
      : [];
  }, [data]);

  const requestedUploadFields = useMemo(() => {
    const mapped = requestedDocuments
      .map((item) => {
        const definition = DOCUMENT_DEFINITION_MAP[normalizeDocumentLabel(item)];

        if (definition) {
          return definition;
        }

        return null;
      })
      .filter(Boolean);

    return mapped.filter(
      (item, index, array) =>
        array.findIndex((entry) => entry.fieldName === item.fieldName) === index
    );
  }, [requestedDocuments]);

  const showDefaultBusinessUpload =
    !data?.followup_request &&
    (data?.is_planned_founding_followup || data?.is_existing_company_followup);

  const shouldShowUploadForm =
    Boolean(data?.can_upload_business_documents) ||
    requestedUploadFields.length > 0 ||
    showDefaultBusinessUpload ||
    data?.identity_status === 'additional_requested' ||
    data?.business_status === 'pending';

  const hasFollowupRequest = Boolean(data?.followup_request);

  const fallbackUploadFields = useMemo(() => {
    if (hasFollowupRequest) {
      return [];
    }

    const fallback = [];

    if (
      data?.business_status === 'pending' &&
      requestedUploadFields.length === 0 &&
      !showDefaultBusinessUpload
    ) {
      fallback.push({ label: businessUploadLabel, fieldName: 'business_doc' });
    }

    if (data?.identity_status === 'additional_requested' && requestedUploadFields.length === 0) {
      fallback.push({ label: 'Zusatzdokument', fieldName: 'extra_doc' });
    }

    return fallback;
  }, [businessUploadLabel, data, hasFollowupRequest, requestedUploadFields, showDefaultBusinessUpload]);

  const visibleUploadFields = useMemo(() => {
    const combined = [...requestedUploadFields, ...fallbackUploadFields];

    return combined.filter(
      (item, index, array) =>
        array.findIndex((entry) => entry.fieldName === item.fieldName) === index
    );
  }, [requestedUploadFields, fallbackUploadFields]);

  const identityMeta = getIdentityStatusMeta(data?.identity_status);
  const businessMeta = getBusinessStatusMeta(data?.business_status);
  const canOpenVerificationForm = ['new', 'pending', 'additional_requested'].includes(
    normalizeUtfText(data?.identity_status).toLowerCase()
  );
  const hasCompletedVerification = hasBusinessHubFeatureAccess({
    identityStatus: data?.identity_status,
    businessStatus: data?.business_status,
    customerType: data?.customer?.customer_type,
  });
  const portalRequests = Array.isArray(data?.portal_requests)
    ? data.portal_requests.filter((request) => request.request_type !== 'display_profile')
    : [];
  const pendingPortalRequests = portalRequests.filter(
    (request) => request.status === 'pending' && request.request_type !== 'company_data'
  );

  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    setFiles((prev) => ({
      ...prev,
      [name]: selectedFiles[0] || null,
    }));
  };

  const handleAgreementDataChange = (e) => {
    const { name, value } = e.target;
    setAgreementData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDisplayDraftChange = (e) => {
    const { name, value } = e.target;
    setDisplayDraft((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const refreshPortalData = async () => {
    const response = await fetch(`${API_URL}/portal/${token}`);
    const json = await response.json();
    if (!response.ok) {
      throw new Error(json.error || t('error_fetch'));
    }
    setData(json);
    setAgreementData(normalizeAgreementData(json.usage_agreement_data));
    setDisplayDraft({
      display_name: normalizeUtfText(json.display_profile?.display_name || json.customer?.company_name || json.customer?.name || ''),
      display_note: normalizeUtfText(json.display_profile?.display_note || ''),
    });
  };

  const submitCompanyChangeRequest = async (e) => {
    e.preventDefault();
    setPortalRequestSubmitting('company_data');

    try {
      const payload = new FormData();
      payload.append('request_type', 'company_data');
      Object.entries(agreementData).forEach(([fieldName, value]) => {
        payload.append(fieldName, value || '');
      });

      const response = await fetch(`${API_URL}/portal/${token}/requests`, {
        method: 'POST',
        body: payload,
      });
      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error || ui('Änderung konnte nicht eingereicht werden.', 'The change could not be submitted.'));
      }

      alert(json.message || ui('Ihre Kundendaten wurden gespeichert.', 'Your account details have been saved.'));
      await refreshPortalData();
    } catch (err) {
      console.error(err);
      alert(err.message || ui('Änderung konnte nicht eingereicht werden.', 'The change could not be submitted.'));
    } finally {
      setPortalRequestSubmitting('');
    }
  };

  const submitDisplayChangeRequest = async (e) => {
    e.preventDefault();
    setPortalRequestSubmitting('display_profile');

    try {
      const payload = new FormData();
      payload.append('request_type', 'display_profile');
      payload.append('display_name', displayDraft.display_name || '');
      payload.append('display_note', displayDraft.display_note || '');
      if (displayLogo) {
        payload.append('logo', displayLogo);
      }

      const response = await fetch(`${API_URL}/portal/${token}/requests`, {
        method: 'POST',
        body: payload,
      });
      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error || ui('Briefkasten & Logo konnte nicht gespeichert werden.', 'The mailbox name and logo could not be saved.'));
      }

      alert(json.message || ui('Briefkasten & Logo wurde gespeichert.', 'The mailbox name and logo have been saved.'));
      setDisplayLogo(null);
      await refreshPortalData();
    } catch (err) {
      console.error(err);
      alert(err.message || ui('Briefkasten & Logo konnte nicht gespeichert werden.', 'The mailbox name and logo could not be saved.'));
    } finally {
      setPortalRequestSubmitting('');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = new FormData();

      Object.entries(files).forEach(([fieldName, file]) => {
        if (file) {
          payload.append(fieldName, file);
        }
      });

      if (showAgreementUpdateFields) {
        Object.entries(agreementData).forEach(([fieldName, value]) => {
          payload.append(fieldName, value || '');
        });
      }

      const response = await fetch(`${API_URL}/portal/${token}/documents`, {
        method: 'POST',
        body: payload,
      });

      const json = await response.json();

      if (response.ok) {
        alert(json.upload_kind === 'requested_followup'
          ? ui('Die angeforderten Unterlagen wurden erfolgreich eingereicht.', 'The requested documents were submitted successfully.')
          : json.upload_kind === 'founding_company_submission'
            ? ui('Ihre Unternehmensunterlagen wurden erfolgreich eingereicht.', 'Your company documents were submitted successfully.')
            : json.usage_agreement_data_updated
              ? t('upload_success_agreement_updated')
              : t('upload_success'));

        setData((prev) => ({
          ...prev,
          identity_status: requestedUploadFields.some((field) => ['id_front', 'id_back', 'selfie'].includes(field.fieldName))
            ? 'in_review'
            : prev?.identity_status,
          business_status: json.upload_kind === 'founding_company_submission' ||
            requestedUploadFields.some((field) => ['business_doc', 'gewerbeanmeldung', 'handelsregisterauszug', 'vollmacht'].includes(field.fieldName))
            ? (prev?.business_status === 'not_required' ? 'not_required' : 'in_review')
            : prev?.business_status,
          can_upload_business_documents: false,
          followup_request: null,
        }));

        setFiles(INITIAL_FILES);
      } else {
        alert(json.error || t('error_upload'));
      }
    } catch (err) {
      console.error(err);
      alert(t('error_upload'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="page-shell">
        <div className="card">
          <p>{t('loading')}</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="page-shell">
        <div className="card">
          <p>{error}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="page-shell">
      <div className="brand-header">
        <div>
          <p className="eyebrow">YourGermanAddress</p>
          <h1>{t('portal_upload_title')}</h1>
          <p className="subtitle">
            {t('portal_upload_subtitle')}
          </p>
        </div>
        <Link className="primary-link-button" to="/account/dashboard">
          {ui('Zurück zum Business Hub', 'Back to Business Hub')}
        </Link>
      </div>

      <div className="card">
        <div className="card-section">
          <h2>{ui('Ihre Verifizierung', 'Your verification')}</h2>
          <p>
            {normalizeUtfText(data.customer?.company_name || data.customer?.name || data.customer?.email)}
          </p>
          {canOpenVerificationForm ? (
            <Link className="primary-link-button" to={`/verify/${encodeURIComponent(token)}`}>
              {ui('Verifizierung öffnen', 'Open verification')}
            </Link>
          ) : null}
        </div>

        <div className="portal-tabs" role="tablist" aria-label="Verifizierungsfall Bereiche">
          {[
            ['overview', ui('Status', 'Status')],
            ['documents', ui('Dokumente', 'Documents')],
            ['company', ui('Daten', 'Details')],
            ['display', ui('Briefkasten', 'Mailbox')],
          ].map(([tabKey, label]) => {
            const locked = !hasCompletedVerification && !['overview', 'documents'].includes(tabKey);
            return (
            <button
              key={tabKey}
              type="button"
              className={`portal-tab ${activeTab === tabKey ? 'portal-tab--active' : ''}`}
              onClick={() => !locked && setActiveTab(tabKey)}
              disabled={locked}
              title={locked ? ui('Nach vollständiger Verifizierung verfügbar', 'Available after verification is complete') : undefined}
            >
              {label}{locked ? ' 🔒' : ''}
            </button>
            );
          })}
        </div>

        {!hasCompletedVerification ? (
          <div className="info-box">{ui('Sobald Ihre Verifizierung abgeschlossen ist, stehen Ihnen alle Funktionen Ihres Business Hubs zur Verfügung.', 'Once your verification is complete, all Business Hub features will be available to you.')}</div>
        ) : null}

        {activeTab === 'company' && (
          <div className="card-section">
            <h2>{ui('Kundendaten', 'Account details')}</h2>
            <p>{ui('Halten Sie Ihre persönlichen und geschäftlichen Angaben selbst aktuell. Gespeicherte Änderungen werden unmittelbar übernommen.', 'Keep your personal and business details up to date. Saved changes are applied immediately.')}</p>
            <form onSubmit={submitCompanyChangeRequest} className="form-grid">
              {Object.keys(INITIAL_AGREEMENT_DATA).map((fieldName) => (
                <label key={fieldName} className={fieldName === 'company_name' || fieldName === 'business_activity' ? 'full-width' : ''}>
                  {AGREEMENT_FIELD_LABELS[fieldName] || fieldName.replace(/_/g, ' ')}
                  <input
                    type={fieldName === 'planned_foundation_date' ? 'date' : 'text'}
                    name={fieldName}
                    value={agreementData[fieldName]}
                    onChange={handleAgreementDataChange}
                  />
                </label>
              ))}
              <div className="full-width">
                <button type="submit" className="primary-button" disabled={portalRequestSubmitting === 'company_data'}>
                  {portalRequestSubmitting === 'company_data' ? ui('Änderungen werden gespeichert...', 'Saving changes...') : ui('Änderungen speichern', 'Save changes')}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'display' && (
          <div className="card-section">
            <h2>{ui('Briefkasten', 'Mailbox')}</h2>
            <p>{ui('Sie bestimmen selbst, welcher Name und welches optionale Logo am Briefkasten angezeigt wird.', 'You choose the name and optional logo shown on the mailbox display.')}</p>
            <div className="info-box">
              <strong>{ui('Hinweise zur Beschriftung', 'Mailbox labelling tips')}</strong>
              <ul>
                <li>{ui('Bei Einzelunternehmen steht üblicherweise der persönliche Vor- und Nachname am Briefkasten; ein Geschäftsname kann ergänzt werden.', 'For sole proprietorships, the owner’s first and last name is usually shown; a business name may be added.')}</li>
                <li>{ui('Bei Gesellschaften sollte die vollständige Firmierung einschließlich Rechtsform erscheinen, zum Beispiel „Muster GmbH“ oder „Muster UG (haftungsbeschränkt)“.', 'Companies should use their full registered name including legal form, for example “Example GmbH” or “Example UG (haftungsbeschränkt)”.')}</li>
                <li>{ui('Wählen Sie eine eindeutige, gut lesbare Bezeichnung, damit Post sicher zugeordnet werden kann.', 'Choose a clear, legible name so mail can be assigned reliably.')}</li>
              </ul>
            </div>
            {data.display_profile ? (
              <div className="info-box">
                <strong>{ui('Aktuell gespeichert:', 'Currently saved:')}</strong>
                <p>{normalizeUtfText(data.display_profile.display_name || '-')}</p>
                {data.display_profile.documents?.original_filename ? (
                  <p>Logo: {normalizeUtfText(data.display_profile.documents.original_filename)}</p>
                ) : null}
              </div>
            ) : null}
            <form onSubmit={submitDisplayChangeRequest} className="form-grid">
              <label className="full-width">
                {ui('Name auf dem Briefkasten', 'Name to display on the mailbox')}
                <input type="text" name="display_name" value={displayDraft.display_name} onChange={handleDisplayDraftChange} />
              </label>
              <label className="full-width">
                {ui('Zusätzlicher Hinweis (optional)', 'Additional note (optional)')}
                <textarea name="display_note" value={displayDraft.display_note} onChange={handleDisplayDraftChange} rows="4" />
              </label>
              <label className="full-width">
                {ui('Logo optional', 'Logo (optional)')}
                <span className="field-hint">
                  {ui('Bitte möglichst quadratisch hochladen, empfohlen 800 x 800 px, PNG oder JPG, maximal 2 MB. Logos mit transparentem Hintergrund wirken am besten.', 'Please upload a square image where possible. Recommended: 800 × 800 px, PNG or JPG, maximum 2 MB. Transparent backgrounds work best.')}
                </span>
                <input type="file" accept="image/*,.pdf" onChange={(event) => setDisplayLogo(event.target.files?.[0] || null)} />
              </label>
              <div className="full-width">
                <button type="submit" className="primary-button" disabled={portalRequestSubmitting === 'display_profile'}>
                  {portalRequestSubmitting === 'display_profile' ? ui('Änderung wird übermittelt...', 'Submitting changes...') : ui('Briefkastenangaben übernehmen', 'Save mailbox details')}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'overview' && (
          <>
        {!data.initial_submission_completed && (
          <div className="warning-box">
            <strong>{ui('Ihre Verifizierung ist noch nicht abgeschlossen', 'Your verification is not complete yet')}</strong>
            <p>
              {ui(
                'Bitte füllen Sie zuerst das vollständige Verifizierungsformular aus und laden Sie Ausweis und Selfie dort hoch.',
                'Please complete the full verification form and upload your ID and selfie there first.'
              )}
            </p>
            <Link className="button-link" to={data.initial_verification_url || `/verify/${token}`}>
              {ui('Verifizierung fortsetzen', 'Continue verification')}
            </Link>
          </div>
        )}
        <div className="status-grid status-grid--stack-mobile">
          <div className="status-box status-box--rich">
            <span>{t('identity_status')}</span>
            <StatusBadge label={identityMeta.label} tone={identityMeta.tone} />
            <p className="status-description">{identityMeta.description}</p>
          </div>
          <div className="status-box status-box--rich">
            <span>{t('business_status')}</span>
            <StatusBadge label={businessMeta.label} tone={businessMeta.tone} />
            <p className="status-description">{businessMeta.description}</p>
          </div>
        </div>

        {data.identity_status === 'approved' && data.business_status === 'approved' && (
          <div className="success-box">
            {t('verification_complete')}
          </div>
        )}

        {pendingPortalRequests.length > 0 && (
          <div className="info-box">
            <strong>{ui('Ihre offenen Anfragen', 'Your open requests')}</strong>
            <ul>
              {pendingPortalRequests.map((request) => (
                <li key={request.id}>
                  {request.request_type === 'display_profile' ? ui('Briefkasten', 'Mailbox') : ui('Daten', 'Details')} - {ui('wird geprüft', 'under review')}
                </li>
              ))}
            </ul>
          </div>
        )}
          </>
        )}

        {activeTab === 'documents' && (
          <>
        {data.followup_request && (
          <div className="info-box">
            <strong>{t('followup_request')}</strong>

            {requestedDocuments.length > 0 && (
              <>
                <p>{t('please_submit_requested_documents')}</p>
                <ul>
                  {requestedDocuments.map((doc) => (
                    <li key={doc}>{getRequestedDocumentDisplayLabel(doc)}</li>
                  ))}
                </ul>
              </>
            )}

            {data.followup_request.requested_message ? (
              <p>
                <strong>{t('note')}:</strong>
                <br />
                {normalizeUtfText(data.followup_request.requested_message)}
              </p>
            ) : null}
          </div>
        )}

        {showDefaultBusinessUpload && (
          <div className="info-box">
            {data.is_planned_founding_followup ? (
              <>
                <strong>{t('founding_followup_title')}</strong>
                <p>
                  {t('founding_followup_text')}
                </p>
              </>
            ) : customerType === 'freelancer' ? (
              <>
                <strong>{t('freelancer_missing_title')}</strong>
                <p>
                  {t('freelancer_missing_text')}
                </p>
              </>
            ) : (
              <>
                <strong>{t('business_docs_missing_title')}</strong>
                <p>
                  {t('business_docs_missing_text')}
                </p>
              </>
            )}
          </div>
        )}

        {shouldShowUploadForm && (
          <div className="card-section">
            <p>{t('please_upload_documents_here')}</p>

            <form onSubmit={handleUpload} className="form-grid">
              {showAgreementUpdateFields && (
                <>
                  <div className="info-box full-width">
                    <strong>{t('check_agreement_data_title')}</strong>
                    <p>
                      {t('check_agreement_data_text')}
                    </p>
                  </div>

                  <label className="full-width">
                    {t('company_name_or_planned')}
                    <input
                      type="text"
                      name="company_name"
                      value={agreementData.company_name}
                      onChange={handleAgreementDataChange}
                      required
                    />
                  </label>

                  <label>
                    {t('legal_form')}
                    <input
                      type="text"
                      name="legal_form"
                      value={agreementData.legal_form}
                      onChange={handleAgreementDataChange}
                      placeholder={t('legal_form_placeholder')}
                      required
                    />
                  </label>

                  <label>
                    {t('representative')}
                    <input
                      type="text"
                      name="representative_name"
                      value={agreementData.representative_name}
                      onChange={handleAgreementDataChange}
                      required
                    />
                  </label>

                  <label className="full-width">
                    {t('business_activity')}
                    <input
                      type="text"
                      name="business_activity"
                      value={agreementData.business_activity}
                      onChange={handleAgreementDataChange}
                      required
                    />
                  </label>

                  <label>
                    {t('company_street_optional')}
                    <input
                      type="text"
                      name="company_street"
                      value={agreementData.company_street}
                      onChange={handleAgreementDataChange}
                    />
                  </label>

                  <label>
                    {t('company_postal_code_optional')}
                    <input
                      type="text"
                      name="company_postal_code"
                      value={agreementData.company_postal_code}
                      onChange={handleAgreementDataChange}
                    />
                  </label>

                  <label>
                    {t('company_city_optional')}
                    <input
                      type="text"
                      name="company_city"
                      value={agreementData.company_city}
                      onChange={handleAgreementDataChange}
                    />
                  </label>

                  <label>
                    {t('company_country_optional')}
                    <input
                      type="text"
                      name="company_country"
                      value={agreementData.company_country}
                      onChange={handleAgreementDataChange}
                    />
                  </label>

                  <label>
                    {t('register_court_optional')}
                    <input
                      type="text"
                      name="register_court"
                      value={agreementData.register_court}
                      onChange={handleAgreementDataChange}
                    />
                  </label>

                  <label>
                    {t('register_number_optional')}
                    <input
                      type="text"
                      name="register_number"
                      value={agreementData.register_number}
                      onChange={handleAgreementDataChange}
                    />
                  </label>

                  <label>
                    {t('tax_number_optional')}
                    <input
                      type="text"
                      name="tax_number"
                      value={agreementData.tax_number}
                      onChange={handleAgreementDataChange}
                    />
                  </label>

                  <label>
                    {t('vat_id_optional')}
                    <input
                      type="text"
                      name="vat_id"
                      value={agreementData.vat_id}
                      onChange={handleAgreementDataChange}
                    />
                  </label>

                  <label className="full-width">
                    {t('planned_foundation_date_optional')}
                    <input
                      type="date"
                      name="planned_foundation_date"
                      value={agreementData.planned_foundation_date}
                      onChange={handleAgreementDataChange}
                    />
                  </label>
                </>
              )}

              {showDefaultBusinessUpload && (
                <label className="full-width">
                  {businessUploadLabel}
                  <input
                    type="file"
                    name="business_doc"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                  />
                </label>
              )}

              {!showDefaultBusinessUpload &&
                visibleUploadFields.map((field) => (
                  <label key={field.fieldName} className="full-width">
                    {getUploadFieldLabel(field)}
                    <input
                      type="file"
                      name={field.fieldName}
                      accept="image/*,.pdf"
                      onChange={handleFileChange}
                    />
                  </label>
                ))}

              <div className="full-width">
                <button type="submit" disabled={submitting} className="primary-button">
                  {submitting ? t('uploading') : t('upload_documents')}
                </button>
              </div>
            </form>
          </div>
        )}

        {!shouldShowUploadForm && (
          <div className="card-section">
            <p>{ui('Aktuell sind keine Dokumente angefordert.', 'No documents are currently requested.')}</p>
          </div>
        )}
          </>
        )}
      </div>
    </main>
  );
}

export default Portal;

